import * as vp from 'vopay';

import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ScheduledTransactions.module.scss';
import { Button } from 'UI';

const { Select, TextField, DateRangePicker } = vp;

type ScheduledTransactionSearchParams = {
  scheduleType: string;
  transactionType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
};

type SearchFormProps = {
  timeRange?: [vp.Timestamp, vp.Timestamp];
  scheduleType: string;
  transactionType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  search: (searchParams: ScheduledTransactionSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const ScheduledTransactionsForm: FC = (props: SearchFormProps) => {
  const {
    scheduleType,
    transactionType,
    frequency,
    status,
    searchTerm,
    initialTimeRange,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    search,
  } = props;

  const [currentScheduleType, setCurrentScheduleType] = useState<string>(scheduleType);
  const [currentTransactionType, setCurrentTransactionType] = useState<string>(transactionType);
  const [currentFrequency, setCurrentFrequency] = useState<string>(frequency);
  const [currentStatus, setCurrentStatus] = useState<string>(status);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.List.SearchForm' });

  const scheduleTypeOption = [
    { value: 'all', label: t('ScheduleType.Options.all') },
    { value: 'single', label: t('ScheduleType.Options.single') },
    { value: 'recurring', label: t('ScheduleType.Options.recurring') },
  ];

  const transactionTypeOption = [
    { value: 'all', label: t('TransactionType.Options.all') },
    { value: 'fund', label: t('TransactionType.Options.fund') },
    { value: 'withdraw', label: t('TransactionType.Options.withdraw') },
  ];

  const frequencyOptions = [
    { value: 'all', label: t('Frequency.Options.all') },
    { value: 'monthly', label: t('Frequency.Options.monthly') },
    { value: 'weekly', label: t('Frequency.Options.weekly') },
    { value: 'semi-monthly', label: t('Frequency.Options.semi-monthly') },
    { value: 'biweekly', label: t('Frequency.Options.biweekly') },
    { value: '3 months', label: t('Frequency.Options.3 months') },
    { value: '6 months', label: t('Frequency.Options.6 months') },
    { value: 'yearly', label: t('Frequency.Options.yearly') },
    { value: 'bimonthly', label: t('Frequency.Options.bimonthly') },
  ];

  const statusOptions = [
    { value: 'all', label: t('Status.Options.all') },
    { value: 'cancelled', label: t('Status.Options.cancelled') },
    { value: 'scheduled', label: t('Status.Options.scheduled') },
    { value: 'completed', label: t('Status.Options.completed') },
    { value: 'in progress', label: t('Status.Options.in progress') },
    { value: 'pending approval', label: t('Status.Options.pending approval') },
  ];

  return (
    <div className={styles.formContainer}>
      <div className={styles.flexUnset}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />
      </div>
      <div className={styles.flex3}>
        <Select
          version="2"
          placeholder={t('ScheduleType.placeholder')}
          options={scheduleTypeOption}
          name="scheduleType"
          value={currentScheduleType}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentScheduleType(e.target.value);
          }}
        />
      </div>
      <div className={styles.flex3}>
        <Select
          version="2"
          placeholder={t('TransactionType.placeholder')}
          options={transactionTypeOption}
          name="transactionType"
          value={currentTransactionType}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentTransactionType(e.target.value);
          }}
        />
      </div>
      <div className={styles.flex3}>
        <Select
          version="2"
          placeholder={t('Frequency.placeholder')}
          options={frequencyOptions}
          name="frequency"
          value={currentFrequency}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentFrequency(e.target.value);
          }}
        />
      </div>
      <div className={styles.flex3}>
        <Select
          version="2"
          placeholder={t('Status.placeholder')}
          options={statusOptions}
          name="status"
          value={currentStatus}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentStatus(e.target.value);
          }}
        />
      </div>
      <div className={styles.flex3}>
        <TextField
          key="SearchKeyword"
          name="SearchKeyword"
          placeholder={t('keywordPlaceholder')}
          value={currentSearchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
        />
      </div>
      <Button
        id="search-scheduled-transactions"
        size="small"
        colorScheme="primary"
        onClick={() => {
          search({
            scheduleType: currentScheduleType,
            transactionType: currentTransactionType,
            searchTerm: currentSearchTerm,
            frequency: currentFrequency,
            status: currentStatus,
            timeRange,
          });
        }}
      >
        {t('searchButtonLabel')}
      </Button>
    </div>
  );
};

export default ScheduledTransactionsForm;
